import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router";
import { toast } from "sonner";
import { db, updateAdminUser, deleteAdminUser } from "@/lib/instantdb";
import { fetchRoles, adminStore, type Role, type User } from "../stores/admin";
import { useSnapshot } from "valtio";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { UserJobsTab } from "../components/UserJobsTab";
import {
  AlertCircle,
  ArrowLeft,
  Mail,
  Calendar,
  Shield,
  UserX,
  Trash2,
  Briefcase,
  User as UserIcon,
} from "lucide-react";

export default function UserDetail() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { roles: availableRoles } = useSnapshot(adminStore);

  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [rolesDialogOpen, setRolesDialogOpen] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [savingRoles, setSavingRoles] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    fetchRoles();
    loadUser();
  }, [userId]);

  const loadUser = async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);

    try {
      const { data } = await db.queryOnce({
        admin_users: {
          $: { where: { id: userId } },
        },
        admin_roles: {},
        admin_scopes: {},
      });

      const users = data?.admin_users ?? [];
      const roles = (data?.admin_roles ?? []) as Array<{
        id: string;
        name: string;
        description: string;
        scopes: unknown;
        createdAt: number;
        updatedAt: number;
      }>;
      const scopes = (data?.admin_scopes ?? []) as Array<{
        id: string;
        name: string;
        description: string;
      }>;

      if (users.length === 0) {
        setError("User not found");
        return;
      }

      const rawUser = users[0] as {
        id: string;
        email: string;
        name: string;
        firstName?: string;
        lastName?: string;
        roles: unknown;
        createdAt: number;
        updatedAt: number;
      };

      // Map roles with their scopes
      const rolesWithScopes = roles.map((r) => {
        const roleScopes = (r.scopes as string[]) ?? [];
        return {
          id: r.id,
          name: r.name,
          description: r.description,
          scopes: scopes.filter((s) => roleScopes.includes(s.id)),
          createdAt: r.createdAt,
          updatedAt: r.updatedAt,
        };
      });

      // Map user with roles
      const userRoleIds = (rawUser.roles as string[]) ?? [];
      const userWithRoles: User = {
        id: rawUser.id,
        authId: rawUser.id,
        email: rawUser.email,
        name: rawUser.name,
        firstName: rawUser.firstName,
        lastName: rawUser.lastName,
        roles: rolesWithScopes.filter((r) => userRoleIds.includes(r.id)),
        createdAt: rawUser.createdAt,
        updatedAt: rawUser.updatedAt,
      };

      setUser(userWithRoles);
      setSelectedRoles(userRoleIds);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load user");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRoles = async () => {
    if (!userId) return;
    setSavingRoles(true);

    try {
      await updateAdminUser(userId, {
        roles: selectedRoles,
      });
      toast.success("User roles updated");
      setRolesDialogOpen(false);
      await loadUser();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to update roles");
    } finally {
      setSavingRoles(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!userId) return;
    setDeleteLoading(true);

    try {
      await deleteAdminUser(userId);
      toast.success("User deleted");
      navigate("/users");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to delete user");
    } finally {
      setDeleteLoading(false);
    }
  };

  const toggleRole = (roleId: string) => {
    setSelectedRoles((prev) =>
      prev.includes(roleId)
        ? prev.filter((id) => id !== roleId)
        : [...prev, roleId],
    );
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="flex h-full flex-col">
        <header className="shrink-0 border-b bg-background px-4 py-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-64 mt-2" />
          </div>
        </header>
        <main className="min-h-0 flex-1 overflow-y-auto">
          <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
            <div className="grid gap-6 md:grid-cols-2">
              <Skeleton className="h-48" />
              <Skeleton className="h-48" />
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="flex h-full flex-col">
        <header className="shrink-0 border-b bg-background px-4 py-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <Button variant="ghost" onClick={() => navigate("/users")}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Users
            </Button>
          </div>
        </header>
        <main className="min-h-0 flex-1 overflow-y-auto">
          <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error || "User not found"}</AlertDescription>
            </Alert>
          </div>
        </main>
      </div>
    );
  }

  const displayName =
    user.firstName || user.lastName
      ? `${user.firstName || ""} ${user.lastName || ""}`.trim()
      : user.name || user.email;

  return (
    <div className="flex h-full flex-col">
      {/* Fixed Header */}
      <header className="shrink-0 border-b bg-background px-4 py-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <Button
            variant="ghost"
            className="mb-4"
            onClick={() => navigate("/users")}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Users
          </Button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">{displayName}</h1>
              <p className="mt-2 text-muted-foreground">{user.email}</p>
            </div>
          </div>
        </div>
      </header>

      {/* Scrollable Content */}
      <main className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <Tabs defaultValue="profile" className="space-y-6">
            <TabsList>
              <TabsTrigger value="profile" className="gap-2">
                <UserIcon className="h-4 w-4" />
                Profile
              </TabsTrigger>
              <TabsTrigger value="roles" className="gap-2">
                <Shield className="h-4 w-4" />
                Roles
              </TabsTrigger>
              <TabsTrigger value="jobs" className="gap-2">
                <Briefcase className="h-4 w-4" />
                Jobs
              </TabsTrigger>
            </TabsList>

            {/* Profile Tab */}
            <TabsContent value="profile" className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                {/* Profile Info */}
                <Card>
                  <CardHeader>
                    <CardTitle>Profile Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center gap-3">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Email</p>
                        <p className="font-medium">{user.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Created</p>
                        <p className="font-medium">{formatDate(user.createdAt)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">
                          Last Updated
                        </p>
                        <p className="font-medium">{formatDate(user.updatedAt)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Danger Zone */}
                <Card className="border-destructive/50">
                  <CardHeader>
                    <CardTitle className="text-destructive">Danger Zone</CardTitle>
                    <CardDescription>
                      Irreversible actions that affect this user account
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex gap-4">
                    <Button
                      variant="outline"
                      className="text-destructive border-destructive/50 hover:bg-destructive/10"
                      onClick={() => {
                        toast.info("Disable user functionality coming soon");
                      }}
                    >
                      <UserX className="h-4 w-4 mr-2" />
                      Disable Account
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => setDeleteDialogOpen(true)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete User
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Roles Tab */}
            <TabsContent value="roles">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Shield className="h-5 w-5" />
                      Assigned Roles
                    </CardTitle>
                    <CardDescription>
                      {user.roles?.length || 0} role(s) assigned
                    </CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setRolesDialogOpen(true)}
                  >
                    Manage Roles
                  </Button>
                </CardHeader>
                <CardContent>
                  {user.roles && user.roles.length > 0 ? (
                    <div className="space-y-3">
                      {user.roles.map((role) => (
                        <div
                          key={role.id}
                          className="flex items-center justify-between p-3 rounded-lg border"
                        >
                          <div>
                            <p className="font-medium">{role.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {role.description}
                            </p>
                          </div>
                          <div className="flex gap-1">
                            {role.scopes?.slice(0, 2).map((scope) => (
                              <Badge
                                key={scope.id}
                                variant="secondary"
                                className="text-xs"
                              >
                                {scope.name}
                              </Badge>
                            ))}
                            {role.scopes && role.scopes.length > 2 && (
                              <Badge variant="outline" className="text-xs">
                                +{role.scopes.length - 2}
                              </Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-4">
                      No roles assigned
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Jobs Tab */}
            <TabsContent value="jobs">
              <UserJobsTab userId={userId!} userEmail={user.email} />
            </TabsContent>
          </Tabs>
        </div>
      </main>

      {/* Manage Roles Dialog */}
      <Dialog open={rolesDialogOpen} onOpenChange={setRolesDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Manage User Roles</DialogTitle>
            <DialogDescription>
              Select which roles to assign to {displayName}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-64 overflow-y-auto rounded-md border p-3 space-y-2">
            {(availableRoles as Role[]).map((role) => (
              <div key={role.id} className="flex items-center space-x-2">
                <Checkbox
                  id={role.id}
                  checked={selectedRoles.includes(role.id)}
                  onCheckedChange={() => toggleRole(role.id)}
                />
                <label
                  htmlFor={role.id}
                  className="flex-1 text-sm cursor-pointer"
                >
                  <span className="font-medium">{role.name}</span>
                  {role.description && (
                    <span className="text-muted-foreground ml-1">
                      - {role.description}
                    </span>
                  )}
                </label>
              </div>
            ))}
            {availableRoles.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">
                No roles available
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRolesDialogOpen(false)}
              disabled={savingRoles}
            >
              Cancel
            </Button>
            <Button onClick={handleUpdateRoles} disabled={savingRoles}>
              {savingRoles ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete User"
        description={`Are you sure you want to delete ${displayName}? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDeleteUser}
        loading={deleteLoading}
      />
    </div>
  );
}
